// Black Friday 2026 sale (Ethan's "BWS+ Black Friday & Q4 Planning", new
// customers): Early Black Friday Nov 9–26 = 30% off + 14 days free, BFCM
// Nov 27–30 = 40%. One switch for the whole site: the Statsig dynamic config
// `bf2026_sale`
//   { enabled, phase: "early" | "bfcm", percent: 30 | 40,
//     starts_at, ends_at (UTC ISO), banner_default: "control" | "variation" }
// The site is in the sale only when `enabled` is true AND now is in
// [starts_at, ends_at). Editing the config needs no deploy.
//
// During the sale every visitor except referral visitors (Core's discount
// route cannot carry a referral code) gets:
//   - the sale banner on quiz pages and pricing (web48's two designs, sale
//     copy, counting down to ends_at), the web48 arm's design when the visitor
//     holds one, otherwise `banner_default`;
//   - the sale price on pricing and checkout: control's price ids plus the
//     BF2026_NEWUSER_* coupon (BLACKFRIDAY30 / BLACKFRIDAY40);
//   - no 10-minute timer and no price flip (web29Offer reads the stored state).
//
// Preview: `?preview=bf-early` forces the early sale on for this browser
// session in any environment; `?preview=bf-off` forces it off; the preview
// build sets REACT_APP_BF_SALE_PREVIEW=bf-early as its default.
import type { StatsigClient, PrecomputedEvaluationsInterface } from "@statsig/js-client";

export const BF_SALE_CONFIG_NAME = "bf2026_sale";
export const BF_SALE_STORAGE_KEY = "bws_bf2026_sale";
export const BF_SALE_PREVIEW_KEY = "bws_bf2026_preview";
export const BF_SALE_PREVIEW_PARAM = "preview";

export type BfSalePhase = "early" | "bfcm";
export type BfSalePercent = 30 | 40;
export type BfBannerDesign = "control" | "variation";
// Amplitude `sale_phase` values.
export type BfSalePhaseTag = "early_bf" | "bfcm" | "none";

export interface BfSale {
  phase: BfSalePhase;
  percent: BfSalePercent;
  // ms epoch; the banner counts down to it and the sale stops there.
  endsAt: number;
  bannerDefault: BfBannerDesign;
  // True when forced by ?preview (or the preview build), not by the config.
  preview: boolean;
}

interface BfSaleConfig {
  enabled: boolean;
  phase: BfSalePhase;
  percent: BfSalePercent;
  startsAt?: number;
  endsAt?: number;
  bannerDefault: BfBannerDesign;
}

// Values a preview uses where the config does not say otherwise.
const PREVIEW_DEFAULTS: Record<BfSalePhase, { percent: BfSalePercent; startsAt: number; endsAt: number }> = {
  // Sun Nov 8 2026 17:00 PST → Thu Nov 26 23:59:59 PST.
  early: { percent: 30, startsAt: Date.parse("2026-11-09T01:00:00Z"), endsAt: Date.parse("2026-11-27T07:59:59Z") },
  // Fri Nov 27 → Mon Nov 30 23:59:59 PST (follow-up phase).
  bfcm: { percent: 40, startsAt: Date.parse("2026-11-27T07:59:59Z"), endsAt: Date.parse("2026-12-01T07:59:59Z") },
};

const PREVIEW_VALUES: Record<string, BfSalePhase | "off"> = {
  "bf-early": "early",
  "bf-off": "off",
};

const parseTime = (value: unknown): number | undefined => {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : undefined;
};

export function parseBfSaleConfig(value: Record<string, unknown> | null | undefined): BfSaleConfig | null {
  if (!value || typeof value !== "object" || Object.keys(value).length === 0) return null;
  const phase: BfSalePhase = value.phase === "bfcm" ? "bfcm" : "early";
  const percent: BfSalePercent = value.percent === 40 ? 40 : value.percent === 30 ? 30 : PREVIEW_DEFAULTS[phase].percent;
  return {
    enabled: value.enabled === true,
    phase,
    percent,
    startsAt: parseTime(value.starts_at),
    endsAt: parseTime(value.ends_at),
    bannerDefault: value.banner_default === "control" ? "control" : "variation",
  };
}

// The preview override for this session: a phase, "off", or null (none).
export function readBfSalePreview(): BfSalePhase | "off" | null {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(BF_SALE_PREVIEW_KEY);
  } catch {
    stored = null;
  }
  const value = stored ?? process.env.REACT_APP_BF_SALE_PREVIEW ?? null;
  return value ? PREVIEW_VALUES[value] ?? null : null;
}

// Runs once at boot (index.tsx) so the param works on any entry URL.
export function syncBfSalePreviewFromLocation(search: string): void {
  let value: string | null;
  try {
    value = new URLSearchParams(search).get(BF_SALE_PREVIEW_PARAM);
  } catch {
    return;
  }
  if (!value || !(value in PREVIEW_VALUES)) return;
  try {
    sessionStorage.setItem(BF_SALE_PREVIEW_KEY, value);
  } catch {
    // Storage blocked: the preview lasts for this page only via the build default.
  }
}

/**
 * The sale this visitor is in right now, or null. Pure: no exposure, no
 * storage writes. `config` is the raw dynamic config value.
 */
export function resolveBfSale(
  config: Record<string, unknown> | null | undefined,
  now: number = Date.now(),
  preview: BfSalePhase | "off" | null = readBfSalePreview()
): BfSale | null {
  if (preview === "off") return null;
  const parsed = parseBfSaleConfig(config);
  if (preview) {
    const defaults = PREVIEW_DEFAULTS[preview];
    const samePhase = parsed?.phase === preview;
    return {
      phase: preview,
      percent: samePhase ? parsed!.percent : defaults.percent,
      endsAt: (samePhase && parsed!.endsAt) || defaults.endsAt,
      bannerDefault: parsed?.bannerDefault ?? "variation",
      preview: true,
    };
  }
  if (!parsed || !parsed.enabled || parsed.startsAt === undefined || parsed.endsAt === undefined) return null;
  if (now < parsed.startsAt || now >= parsed.endsAt) return null;
  return {
    phase: parsed.phase,
    percent: parsed.percent,
    endsAt: parsed.endsAt,
    bannerDefault: parsed.bannerDefault,
    preview: false,
  };
}

export function getBfSale(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined,
  now: number = Date.now()
): BfSale | null {
  // Without a client a preview still applies (it needs no config).
  const config = client ? client.getDynamicConfig(BF_SALE_CONFIG_NAME)?.value : undefined;
  return resolveBfSale(config as Record<string, unknown> | undefined, now);
}

export function toSalePhaseTag(sale: BfSale | null): BfSalePhaseTag {
  if (!sale) return "none";
  return sale.phase === "bfcm" ? "bfcm" : "early_bf";
}

// ----- stored state --------------------------------------------------------
// App writes the resolved sale whenever Statsig is ready and clears it when
// there is none, so web29Offer (synchronous, no client) can drop the
// 10-minute window and its price flip while the sale runs.
interface StoredBfSale {
  phase: BfSalePhase;
  percent: BfSalePercent;
  endsAt: number;
}

export function writeStoredBfSale(sale: BfSale | null): void {
  try {
    if (sale) {
      const stored: StoredBfSale = { phase: sale.phase, percent: sale.percent, endsAt: sale.endsAt };
      localStorage.setItem(BF_SALE_STORAGE_KEY, JSON.stringify(stored));
    } else {
      localStorage.removeItem(BF_SALE_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable: the baseline offer applies until Statsig resolves.
  }
}

// The stored sale while it has not ended; null otherwise.
export function readStoredBfSale(now: number = Date.now()): StoredBfSale | null {
  try {
    const raw = localStorage.getItem(BF_SALE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.endsAt !== "number" || now >= parsed.endsAt) return null;
    if (parsed.phase !== "early" && parsed.phase !== "bfcm") return null;
    return { phase: parsed.phase, percent: parsed.percent === 40 ? 40 : 30, endsAt: parsed.endsAt };
  } catch {
    return null;
  }
}
